/**
 * File Configuration Section Component
 *
 * Purpose: Configuration section for file-type field definitions
 *
 * Architecture: Displays within FieldDefinitionModal when datatype is 'file'
 *
 * Dependencies: React, Material-UI
 * Author: Development Team
 * Created: 2025-01-27
 */

import React from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  FormHelperText,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

// Default file extensions by category
const FILE_EXTENSION_PRESETS = {
  documents: [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'odt',
    'ods',
    'odp',
    'rtf',
    'txt',
  ],
  images: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'],
  videos: ['mp4', 'avi', 'mov', 'mkv', 'webm', 'flv', 'wmv', 'm4v'],
  audio: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'],
  archives: ['zip', 'rar', '7z', 'tar', 'gz'],
};

// All available extensions for autocomplete
const ALL_EXTENSIONS = [
  ...FILE_EXTENSION_PRESETS.documents,
  ...FILE_EXTENSION_PRESETS.images,
  ...FILE_EXTENSION_PRESETS.videos,
  ...FILE_EXTENSION_PRESETS.audio,
  ...FILE_EXTENSION_PRESETS.archives,
].sort();

// Default file config
export const DEFAULT_FILE_CONFIG: FileConfig = {
  allowed_extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif'],
  max_size_mb: 50,
  max_files: 1,
  generate_thumbnails: true,
  allowed_mime_types: null,
};

export interface FileConfig {
  allowed_extensions: string[];
  max_size_mb: number;
  max_files: number;
  generate_thumbnails: boolean;
  allowed_mime_types: string[] | null;
}

interface FileConfigurationSectionProps {
  config: FileConfig | null;
  onChange: (config: FileConfig) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
}

const FileConfigurationSection: React.FC<FileConfigurationSectionProps> = ({
  config,
  onChange,
  disabled = false,
  errors = {},
}) => {
  // Use default config if none provided
  const currentConfig = config || DEFAULT_FILE_CONFIG;
  const [expanded, setExpanded] = React.useState(false);
  const [advancedExpanded, setAdvancedExpanded] = React.useState(false);

  const handleExtensionsChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    onChange({
      ...currentConfig,
      allowed_extensions: newValue.map((ext) => ext.toLowerCase().replace(/^\./, '')),
    });
  };

  const handleMaxSizeChange = (_event: Event, newValue: number | number[]) => {
    onChange({
      ...currentConfig,
      max_size_mb: newValue as number,
    });
  };

  const handleMaxFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 20) {
      onChange({
        ...currentConfig,
        max_files: value,
      });
    }
  };

  const handleMimeTypesChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    onChange({
      ...currentConfig,
      allowed_mime_types: newValue.length > 0 ? newValue : null,
    });
  };

  // Quick preset buttons
  const applyPreset = (presetName: keyof typeof FILE_EXTENSION_PRESETS) => {
    onChange({
      ...currentConfig,
      allowed_extensions: [...FILE_EXTENSION_PRESETS[presetName]],
    });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      data-testid="file-configuration-section"
      sx={{ mt: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">File Upload Configuration</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Allowed Extensions */}
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            multiple
            freeSolo
            options={ALL_EXTENSIONS}
            value={currentConfig.allowed_extensions}
            onChange={handleExtensionsChange}
            disabled={disabled}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={`.${option}`} size="small" {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed File Extensions"
                placeholder="Add extension..."
                error={!!errors.allowed_extensions}
                helperText={
                  errors.allowed_extensions ||
                  'Select allowed file extensions. Type a custom extension and press Enter to add.'
                }
                data-testid="allowed-extensions-input"
              />
            )}
          />

          {/* Quick preset buttons */}
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="textSecondary" sx={{ width: '100%' }}>
              Quick presets:
            </Typography>
            {Object.keys(FILE_EXTENSION_PRESETS).map((preset) => (
              <Chip
                key={preset}
                label={preset.charAt(0).toUpperCase() + preset.slice(1)}
                size="small"
                variant="outlined"
                onClick={() => applyPreset(preset as keyof typeof FILE_EXTENSION_PRESETS)}
                disabled={disabled}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* Max File Size */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Maximum File Size: {currentConfig.max_size_mb} MB
          </Typography>
          <Slider
            value={currentConfig.max_size_mb}
            onChange={handleMaxSizeChange}
            min={1}
            max={100}
            marks={[
              { value: 1, label: '1 MB' },
              { value: 25, label: '25 MB' },
              { value: 50, label: '50 MB' },
              { value: 75, label: '75 MB' },
              { value: 100, label: '100 MB' },
            ]}
            disabled={disabled}
            data-testid="max-file-size-slider"
          />
          <FormHelperText error={!!errors.max_size_mb}>
            {errors.max_size_mb || 'Maximum size for each uploaded file'}
          </FormHelperText>
        </Box>

        {/* Max Files */}
        <Box sx={{ mb: 3 }}>
          <TextField
            type="number"
            label="Maximum Number of Files"
            value={currentConfig.max_files}
            onChange={handleMaxFilesChange}
            inputProps={{ min: 1, max: 20 }}
            disabled={disabled}
            fullWidth
            error={!!errors.max_files}
            helperText={errors.max_files || 'Maximum number of files allowed for this field (1-20)'}
            data-testid="max-files-input"
          />
        </Box>

        {/* Advanced: MIME Types */}
        <Accordion
          expanded={advancedExpanded}
          onChange={(_, isExpanded) => setAdvancedExpanded(isExpanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Advanced Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Autocomplete
                multiple
                freeSolo
                options={[
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.ms-excel',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'image/jpeg',
                  'image/png',
                  'image/gif',
                  'video/mp4',
                  'video/quicktime',
                ]}
                value={currentConfig.allowed_mime_types || []}
                onChange={handleMimeTypesChange}
                disabled={disabled}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip key={key} label={option} size="small" {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Allowed MIME Types (Advanced)"
                    placeholder="Add MIME type..."
                    error={!!errors.allowed_mime_types}
                    helperText={
                      errors.allowed_mime_types ||
                      'Optional: Specify exact MIME types for more precise validation. Leave empty to use extension-based validation only.'
                    }
                    data-testid="allowed-mime-types-input"
                  />
                )}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </AccordionDetails>
    </Accordion>
  );
};

export default FileConfigurationSection;
